/* eslint-disable max-lines */
/* eslint sort-keys: ["error"] */
import { z } from 'zod';
import { locationSchema } from '../location.deployParam';
import { ArgoUdfColumnDataTypeEnum, FisDataTimingEnum } from '../../../../db';
import { type CalendarSubcribedColumns, calendarSubcribedColumnsSchema, calendarUdfColumnSchema } from './nodes/calendar.zod';
import { sparkplugAppletNodeCfgDeployParamSchema } from '../sparkplug-node-cfg.zod';
import { workOrderCfgSchema, workOrderSocketCfg } from './nodes/work-order.node-cfg';
import {
    type BaseNodeCfg,
    type NodeDataSocketKind,
    type NodeKind,
    type NodeSocketCfg,
    type NodeSocketDef,
    type NodeSocketKind,
    baseNodeCfgSchema,
} from './nodes/base-node';
import { workInstructionNodeCfgSchema, workInstructionNodeSocketCfg } from './nodes/work-instruction.zod';
import { materialAssemblyNodeCfg, materialAssemblyNodeCfgSchema } from './nodes/material-assembly.zod';
import { materialComparisonNodeCfg, materialComparisonNodeCfgSchema } from './nodes/material-comparison.zod';
import { materialDisassemblyNodeCfg, materialDisassemblyNodeCfgSchema } from './nodes/material-disassembly.zod';
import { processCollectNodeCfgSchema, processCollectNodeSocketCfg } from './nodes/process-collect.zod';
import { workOrderSetStatusCfgSchema, workOrderSetStatusSocketCfg } from './nodes/work-order-set-status.zod';
import { updateMaterialStatusCfgSchema, updateMaterialStatusSocketCfg } from './nodes/update-material-status.zod';
import { goToTabNodeCfgSchema, goToTabNodeSocketCfg } from './nodes/go-to-tab.zod';
import { nextTabNodeCfgSchema, nextTabNodeSocketCfg } from './nodes/next-tab.zod';
import { previousTabNodeCfgSchema, previousTabNodeSocketCfg } from './nodes/previous-tab.zod';
import { assetSchema } from '../asset.deployParam';
import { materialLookupNodeCfgSchema, materialLookupNodeSocketCfg } from './nodes/material-lookup.node-cfg';
import { mqttSubscribeNodeCfgSchema, mqttSubscribeNodeSocketCfg } from './nodes/mqtt-subscribe.zod';
import { functionalSelectNodeCfgSchema, functionalSelectNodeSocketCfg } from './nodes/functional-select.zod';
import { functionalInputsNodeCfgSchema, functionalInputsNodeSocketCfg } from './nodes/functional-inputs.zod';
import { functionalOutputsNodeCfgSchema, functionalOutputsNodeSocketCfg } from './nodes/functional-outputs.zod';
import { booleanComparisonNodeCfg, booleanComparisonNodeCfgSchema } from './nodes/boolean-comparison.zod';
import { timestampNodeCfgSchema, timestampNodeSocketCfg } from './nodes/timestamp.zod';
import { fileReadNodeCfg, fileReadNodeCfgSchema } from './nodes/file-read.zod';
import { fileWriteNodeCfg, fileWriteNodeCfgSchema } from './nodes/file-write.zod';

export * from './nodes/base-node';

export const nodeCfgSchemaMap = {
    appletEventEmit: baseNodeCfgSchema.extend({
        controlValues: z.object({
            appletEventId: z.string(),
        }),
        kind: z.literal('appletEventEmit'),
    }),
    appletEventListen: baseNodeCfgSchema.extend({
        controlValues: z.object({
            appletEventId: z.string(),
        }),
        kind: z.literal('appletEventListen'),
    }),
    attributeMaterialLink: baseNodeCfgSchema.extend({
        controlValues: z.object({
            attributeId: z.string(),
        }),
        kind: z.literal('attributeMaterialLink'),
    }),
    boolCast: baseNodeCfgSchema.extend({
        kind: z.literal('boolCast'),
    }),
    boolean: baseNodeCfgSchema.extend({
        controlValues: z.object({
            value: z.boolean(),
        }),
        kind: z.literal('boolean'),
    }),
    booleanComparison: booleanComparisonNodeCfgSchema,
    calendar: baseNodeCfgSchema.extend({
        controlValues: z.object({
            location: locationSchema,
            subcribedColumns: calendarSubcribedColumnsSchema,
            udfColumns: z.record(calendarUdfColumnSchema),
        }),
        kind: z.literal('calendar'),
    }),
    comparisonNumber: baseNodeCfgSchema.extend({
        controlValues: z.object({
            not: z.boolean(),
            operation: z.enum([ '=', '>', '>=', '<', '<=' ]),
        }),
        kind: z.literal('comparisonNumber'),
    }),
    comparisonString: baseNodeCfgSchema.extend({
        controlValues: z.object({
            not: z.boolean(),
            operation: z.enum([ '=', 'contains', 'startsWith', 'endsWith' ]),
        }),
        kind: z.literal('comparisonString'),
    }),
    createMaterial: baseNodeCfgSchema.extend({
        controlValues: z.object({
            location: locationSchema,
            materialModelId: z.string(),
            snCfg: z.discriminatedUnion('kind', [
                z.object({ kind: z.literal('direct') }),
                z.object({
                    fmtInputs: z.array(z.string()), // this is to generate the inputs for the formatter
                    kind: z.literal('formatter'),
                }),
            ]),
            udfs: z.record(z.string(), z.object({
                name: z.string(),
                required: z.boolean(),
                type: z.nativeEnum(ArgoUdfColumnDataTypeEnum),
            })),
        }),
        kind: z.literal('createMaterial'),
    }),
    defaultVal: baseNodeCfgSchema.extend({
        kind: z.literal('defaultVal'),
    }),
    eventDelay: baseNodeCfgSchema.extend({
        controlValues: z.object({
            delayMs: z.number(),
        }),
        kind: z.literal('eventDelay'),
    }),
    eventOneShot: baseNodeCfgSchema.extend({
        kind: z.literal('eventOneShot'),
    }),
    eventSwitch: baseNodeCfgSchema.extend({
        controlValues: z.object({
            conditionInputs: z.number(),
            logicalOperator: z.enum([ 'and', 'or' ]),
        }).default({
            conditionInputs: 1,
            logicalOperator: 'and',
        }),
        kind: z.literal('eventSwitch'),
    }),
    eventThrottle: baseNodeCfgSchema.extend({
        controlValues: z.object({
            max: z.number(),
            windowMs: z.number(),
        }),
        kind: z.literal('eventThrottle'),
    }),
    fileRead: fileReadNodeCfgSchema,
    fileWrite: fileWriteNodeCfgSchema,
    functionalInputs: functionalInputsNodeCfgSchema,
    functionalOutputs: functionalOutputsNodeCfgSchema,
    functionalSelect: functionalSelectNodeCfgSchema,
    goToTab: goToTabNodeCfgSchema,
    jsonAgg: baseNodeCfgSchema.extend({
        controlValues: z.object({
            outputExpressions: z.array(z.string().regex(/^[0-9a-z]+$/ui, 'Only simple identifiers are allowed in output expressions')),
        }),
        kind: z.literal('jsonAgg'),
    }),
    jsonParse: baseNodeCfgSchema.extend({
        controlValues: z.object({
            outputExpressions: z.array(z.string().regex(/^[0-9a-z]+$/ui, 'Only simple identifiers are allowed in output expressions')),
        }),
        kind: z.literal('jsonParse'),
    }),
    materialAssembly: materialAssemblyNodeCfgSchema,
    materialComparison: materialComparisonNodeCfgSchema,
    materialDisassembly: materialDisassemblyNodeCfgSchema,
    materialLookup: materialLookupNodeCfgSchema,
    mqttPublish: baseNodeCfgSchema.extend({
        controlValues: z.object({
            qos: z.union([
                z.literal(0),
                z.literal(1),
                z.literal(2),
            ]),
            retain: z.boolean(),
        }),
        kind: z.literal('mqttPublish'),
    }),
    mqttSubscribe: mqttSubscribeNodeCfgSchema,
    nextTab: nextTabNodeCfgSchema,
    not: baseNodeCfgSchema.extend({
        kind: z.literal('not'),
    }),
    numCast: baseNodeCfgSchema.extend({
        kind: z.literal('numCast'),
    }),
    numClamp: baseNodeCfgSchema.extend({
        controlValues: z.object({
            max: z.number().optional(),
            min: z.number().optional(),
        }),
        kind: z.literal('numClamp'),
    }),
    number: baseNodeCfgSchema.extend({
        controlValues: z.object({
            value: z.number(),
        }),
        kind: z.literal('number'),
    }),
    performanceDataCollect: baseNodeCfgSchema.extend({
        controlValues: z.object({
            assetCfg: assetSchema,
            dataLabelId: z.string(),
            timing: z.literal(FisDataTimingEnum.Start).catch(FisDataTimingEnum.Start), // we're not sure if we want to expose the End timing yet
        }),
        kind: z.literal('performanceDataCollect'),
    }),
    previousTab: previousTabNodeCfgSchema,
    processCollect: processCollectNodeCfgSchema,
    script: baseNodeCfgSchema.extend({
        controlValues: z.object({
            scriptId: z.string(),
        }),
        kind: z.literal('script'),
    }),
    slice: baseNodeCfgSchema.extend({
        kind: z.literal('slice'),
    }),
    sparkplugCommander: baseNodeCfgSchema.extend({
        controlValues: z.object({
            configDeploymentParameter: sparkplugAppletNodeCfgDeployParamSchema,
            metricExpressions: z.array(z.string()),
        }),
        kind: z.literal('sparkplugCommander'),
    }),
    sparkplugConsumer: baseNodeCfgSchema.extend({
        controlValues: z.object({
            configDeploymentParameter: sparkplugAppletNodeCfgDeployParamSchema,
            metricExpressions: z.array(z.string()),
        }),
        kind: z.literal('sparkplugConsumer'),
    }),
    start: baseNodeCfgSchema.extend({
        kind: z.literal('start'),
    }),
    strCast: baseNodeCfgSchema.extend({
        kind: z.literal('strCast'),
    }),
    text: baseNodeCfgSchema.extend({
        controlValues: z.object({
            value: z.string(),
        }),
        kind: z.literal('text'),
    }),
    timer: baseNodeCfgSchema.extend({
        controlValues: z.object({
            dir: z.union([ z.literal('up'), z.literal('down') ]),
            /** the number the timer should stop at */
            limit: z.number(),
        }),
        kind: z.literal('timer'),
    }),
    timestamp: timestampNodeCfgSchema,
    txtFormat: baseNodeCfgSchema.extend({
        controlValues: z.object({
            formatTemplate: z.string(),
            inputs: z.number(),
        }),
        kind: z.literal('txtFormat'),
    }),
    udpRead: baseNodeCfgSchema.extend({
        controlValues: z.object({
            encoding: z.union([ z.literal('utf8'), z.literal('hex') ]),
            hostInterface: z.string().min(1),
            port: z.number().min(1024),
        }),
        kind: z.literal('udpRead'),
    }),
    udpSocket: baseNodeCfgSchema.extend({
        controlValues: z.object({
            bindAddress: z.string(),
            bindPort: z.number(),
            connectToAddress: z.string(),
            connectToPort: z.number(),
            encoding: z.union([ z.literal('utf8'), z.literal('hex'), z.literal('base64') ]),
        }),
        kind: z.literal('udpSocket'),
    }),
    udpWrite: baseNodeCfgSchema.extend({
        controlValues: z.object({
            host: z.string().min(1),
            port: z.number().min(1024),
        }),
        kind: z.literal('udpWrite'),
    }),
    updateMaterialStatus: updateMaterialStatusCfgSchema,
    varAssigned: baseNodeCfgSchema.extend({
        controlValues: z.object({
            changesOnly: z.boolean(),
            varId: z.string(),
        }),
        kind: z.literal('varAssigned'),
    }),
    varRead: baseNodeCfgSchema.extend({
        controlValues: z.object({
            varId: z.string(),
        }),
        kind: z.literal('varRead'),
    }),
    varWrite: baseNodeCfgSchema.extend({
        controlValues: z.object({
            varId: z.string(),
        }),
        kind: z.literal('varWrite'),
    }),
    varWriteMultiple: baseNodeCfgSchema.extend({
        controlValues: z.object({
            vars: z.array(z.string()),
        }),
        kind: z.literal('varWriteMultiple'),
    }),
    workInstruction: workInstructionNodeCfgSchema,
    workOrder: workOrderCfgSchema,
    workOrderSetStatus: workOrderSetStatusCfgSchema,
} satisfies {
    // ensure all node schemas don't override anything from baseNodeCfgSchema, and all node kinds are covered.
    [nodeKind in NodeKind]: z.ZodType<BaseNodeCfg & { kind: NodeKind }>;
};

export const nodeCfgSchema: z.ZodType<BaseNodeCfg> = baseNodeCfgSchema.transform((val, ctx) => {
    // this will check the specific schema for the node kind.
    // by not using discriminated union, we can easily ensure that all node kinds are covered,
    // just by propagating the issues up.

    // also we can validate that the socket overrides are valid.

    const specificSchema = nodeCfgSchemaMap[val.kind];
    if (!val.disabled) {
        if (val.kind === 'performanceDataCollect' && val.socketDefaults?.inputs?.assetId && !val.controlValues?.assetCfg) {
            // this is a breaking change that we originally thought we could ignore, but it didn't work out
            const {
                assetId,
                ...newSocketDefaultInputs
            } = val.socketDefaults.inputs;
            val = {
                ...val,
                controlValues: {
                    ...val.controlValues,
                    assetCfg: {
                        id: assetId,
                    },
                },
                socketDefaults: {
                    ...val.socketDefaults,
                    inputs: newSocketDefaultInputs,
                },
            };
        }
        
        const parseResult = specificSchema.safeParse(val, {
            refinementCtxExtra: ctx.extra,
        });
        if (!parseResult.success) {
            const nodeTitle = val.label ? `${val.kind} node "${val.label}"` : `${val.kind} node`;
            for (const issue of parseResult.error.issues) {
                ctx.addIssue({
                    ...issue,
                    fatal: true,
                    message: `${nodeTitle} - ${issue.message}`,
                });
            }
            return z.NEVER;
        }
        return parseResult.data as BaseNodeCfg;
    }

    // TODO validate socketDefaults
    return val as BaseNodeCfg;
});

export type NodeCfg<KIND extends NodeKind> = z.infer<typeof nodeCfgSchemaMap[KIND]>;

/**
 * a mapping from node kind to its sockets
 * @note it should be assumed that the value of every input is nullable
 * @TODO this could be more concise, I'm not sure we need to separate the control and data socket objects here, because we can identify the control sockets with the `event` kind.
 */
export const nodeSocketKindMap = {
    appletEventEmit: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    appletEventListen: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({ event: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    attributeMaterialLink: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({ materialId: 'string' }),
            outputs: () => ({}),
        },
    },
    boolCast: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input: 'varValue' }),
            outputs: () => ({ output: 'boolean' }),
        },
    },
    boolean: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({ output: 'boolean' }),
        },
    },
    booleanComparison: booleanComparisonNodeCfg,
    calendar: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({ event: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: (cfg: NodeCfg<'calendar'>): Partial<Record<`udfColumns_${string}` | keyof CalendarSubcribedColumns, 'number' | 'string'>> => {
                const ret: Partial<Record<`udfColumns_${string}` | keyof CalendarSubcribedColumns, 'number' | 'string'>> = {};
                const possibleOutputs: {
                    [K in keyof CalendarSubcribedColumns]: 'number' | 'string';
                } = {
                    nonProdCurrMetaName: 'string',
                    nonProdCurrMetaTimeEnd: 'string',
                    nonProdCurrMetaTimeStart: 'string',
                    nonProdCurrTimeElapsedSeconds: 'number',
                    nonProdCurrTimeRemainingSeconds: 'number',
                    nonProdNextMetaName: 'string',
                    nonProdNextMetaTimeEnd: 'string',
                    nonProdNextMetaTimeStart: 'string',
                    nonProdNextTimeSecondsUntil: 'number',
                    // rawCalenderUdfData: 'string',
                    shiftCurrMetaName: 'string',
                    shiftCurrMetaTimeEnd: 'string',
                    shiftCurrMetaTimeStart: 'string',
                    shiftCurrTimeElapsedProductive: 'number',
                    shiftCurrTimeElapsedSeconds: 'number',
                    shiftCurrTimeRemainingProductive: 'number',
                    shiftCurrTimeRemainingSeconds: 'number',
                    shiftNextMetaName: 'string',
                    shiftNextMetaTimeEnd: 'string',
                    shiftNextMetaTimeStart: 'string',
                    shiftNextTimeSecondsUntil: 'number',
                };
                for (const key of Object.keys(cfg.controlValues.subcribedColumns) as (keyof CalendarSubcribedColumns)[]) {
                    if (cfg.controlValues.subcribedColumns[key]) {
                        ret[key] = possibleOutputs[key];
                    }
                }
                for (const udfId in cfg.controlValues.udfColumns) {
                    ret[`udfColumns_${udfId}`] = 'number';
                }
                return ret;
            },
        },
    },
    comparisonNumber: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input1: 'number', input2: 'number' }),
            outputs: () => ({ output: 'boolean' }),
        },
    },
    comparisonString: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input1: 'string', input2: 'string' }),
            outputs: () => ({ output: 'boolean' }),
        },
    },
    createMaterial: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: (cfg: NodeCfg<'createMaterial'>): Record<`udfInput_${string}` | 'sn' | `snInput_${string}` | 'workOrderId', 'varValue' | 'string'> => {
                const inputs = {
                    workOrderId: 'string',
                } as Record<`udfInput_${string}` | 'sn' | `snInput_${string}` | 'workOrderId', 'varValue' | 'string'>;

                for (const udfId in cfg.controlValues.udfs) {
                    inputs[`udfInput_${udfId}`] = 'varValue';
                }

                if (cfg.controlValues.snCfg.kind === 'direct') {
                    inputs.sn = 'varValue';
                } else {
                    for (const fmtInput of cfg.controlValues.snCfg.fmtInputs) {
                        inputs[`snInput_${fmtInput}`] = 'varValue';
                    }
                }
                return inputs;
            },
            outputs: () => ({ materialId: 'string', serialNumber: 'string' }),
        },
    },
    defaultVal: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ defaultValue: 'varValue', input: 'varValue' }),
            outputs: () => ({ output: 'varValue' }),
        },
    },
    eventDelay: {
        controlSockets: {
            inputs: () => ({ input: 'event' }),
            outputs: () => ({ output: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    eventOneShot: {
        controlSockets: {
            inputs: () => ({ input: 'event', reset: 'event' }),
            outputs: () => ({ output: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    eventSwitch: {
        controlSockets: {
            inputs: () => ({ event: 'event' }),
            outputs: () => ({ false: 'event', true: 'event' }),
        },
        dataSockets: {
            inputs: (cfg: NodeCfg<'eventSwitch'>): Record<'condition' | `condition${number}`, 'varValue'> => {
                const inputs: Record<'condition' | `condition${number}`, 'varValue'> = {
                    condition: 'varValue',
                };
                for (let i = 1; i < cfg.controlValues.conditionInputs + 1; i++) {
                    inputs[`condition${i}`] = 'varValue';
                }
                return inputs;
            },
            outputs: () => ({}),
        },
    },
    eventThrottle: {
        controlSockets: {
            inputs: () => ({ input: 'event' }),
            outputs: () => ({ output: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    fileRead: fileReadNodeCfg,
    fileWrite: fileWriteNodeCfg,
    functionalInputs: functionalInputsNodeSocketCfg,
    functionalOutputs: functionalOutputsNodeSocketCfg,
    functionalSelect: functionalSelectNodeSocketCfg,
    goToTab: goToTabNodeSocketCfg,
    jsonAgg: {
        controlSockets: {
            inputs: () => ({
                reset: 'event',
                trigger: 'event',
            }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({ input: 'string' }),
            outputs: (cfg: NodeCfg<'jsonAgg'>): {
                merged: 'string',
                [ key: `output${number}` ]: 'varValue';
            } => {
                const outputs: {
                    merged: 'string',
                    [ key: `output${number}` ]: 'varValue';
                } = {
                    merged: 'string',
                };
                for (let i = 0; i < cfg.controlValues.outputExpressions.length; i++) {
                    outputs[`output${i}`] = 'varValue';
                }
                return outputs;
            },
        },
    },
    jsonParse: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input: 'string' }),
            outputs: (cfg: NodeCfg<'jsonParse'>): Record<`output${number}`, 'varValue'> => {
                const outputEntries = cfg.controlValues.outputExpressions.map((_, i) => [ `output${i}`, 'varValue' ]);
                return Object.fromEntries(outputEntries);
            },
        },
    },
    materialAssembly: materialAssemblyNodeCfg,
    materialComparison: materialComparisonNodeCfg,
    materialDisassembly: materialDisassemblyNodeCfg,
    materialLookup: materialLookupNodeSocketCfg,
    mqttPublish: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({ payload: 'string', topic: 'string' }),
            outputs: () => ({}),
        },
    },
    mqttSubscribe: mqttSubscribeNodeSocketCfg,
    nextTab: nextTabNodeSocketCfg,
    not: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input: 'boolean' }),
            outputs: () => ({ output: 'boolean' }),
        },
    },
    numCast: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input: 'varValue' }),
            outputs: () => ({ output: 'number' }),
        },
    },
    numClamp: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input: 'number' }),
            outputs: () => ({ output: 'number' }),
        },
    },
    number: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({ output: 'number' }),
        },
    },
    performanceDataCollect: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({ numericValue: 'number' }),
            outputs: () => ({}),
        },
    },
    previousTab: previousTabNodeSocketCfg,
    processCollect: processCollectNodeSocketCfg,
    script: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    slice: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ end: 'number', input: 'string', start: 'number' }),
            outputs: () => ({ output: 'string' }),
        },
    },
    sparkplugCommander: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ event: 'event' }),
        },
        dataSockets: {
            inputs: (cfg: NodeCfg<'sparkplugCommander'>): Record<`input${number}`, 'varValue'> => {
                const ret = cfg.controlValues.metricExpressions.reduce((acc, _, i) => {
                    acc[`input${i}`] = 'varValue';
                    return acc;
                }, {} as Record<`input${number}`, 'varValue'>);
                return ret;
            },
            outputs: () => ({}),
        },
    },
    sparkplugConsumer: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({ event: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: (cfg: NodeCfg<'sparkplugConsumer'>): Record<`output${number}`, 'varValue'> => {
                const outputEntries = cfg.controlValues.metricExpressions.map((_, i) => [ `output${i}`, 'varValue' ]);
                return Object.fromEntries(outputEntries);
            },
        },
    },
    start: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
    },
    strCast: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({ input: 'anyData' }),
            outputs: () => ({ output: 'string' }),
        },
    },
    text: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({ output: 'string' }),
        },
    },
    timer: {
        controlSockets: {
            inputs: () => ({ reset: 'event', start: 'event', stop: 'event' }),
            outputs: () => ({ done: 'event', tick: 'event' }),
        },
        dataSockets: {
            inputs: () => ({ initialValue: 'number' }),
            outputs: () => ({ value: 'number' }),
        },
    },
    timestamp: timestampNodeSocketCfg,
    txtFormat: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: (cfg: NodeCfg<'txtFormat'>): Record<`input${number}`, 'varValue'> => {
                const inputEntries: [`input${number}`, 'varValue'][] = [];
                for (let i = 0; i < cfg.controlValues.inputs; i++) {
                    inputEntries.push([ `input${i}`, 'varValue' ]);
                }
                return Object.fromEntries(inputEntries);
            },
            outputs: () => ({ output: 'string' }),
        },
    },
    udpRead: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({ event: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({ payload: 'string' }),
        },
    },
    udpSocket: {
        controlSockets: {
            inputs: () => ({ inputTrigger: 'event' }),
            outputs: () => ({
                onReceivedTrigger: 'event',
                onSentTrigger: 'event',
            }),
        },
        dataSockets: {
            inputs: () => ({ sendPayload: 'string' }),
            outputs: () => ({ receivePayload: 'string' }),
        },
    },
    udpWrite: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: () => ({ payload: 'string' }),
            outputs: () => ({}),
        },
    },
    updateMaterialStatus: updateMaterialStatusSocketCfg,
    varAssigned: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({ trigger: 'event' }),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: () => ({ newValue: 'varValue' as NodeDataSocketKind, oldValue: 'varValue' as NodeDataSocketKind }),
        },
    },
    varRead: {
        controlSockets: {
            inputs: () => ({}),
            outputs: () => ({}),
        },
        dataSockets: {
            inputs: () => ({}),
            outputs: (): { value: NodeDataSocketKind } => ({
                value: 'varValue' as NodeDataSocketKind,
            }),
        },
    },
    varWrite: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: (): { value: 'string' | 'number' | 'boolean' | 'varValue' } => ({
                value: 'varValue',
            }),
            outputs: () => ({}),
        },
    },
    varWriteMultiple: {
        controlSockets: {
            inputs: () => ({ trigger: 'event' }),
            outputs: () => ({ next: 'event' }),
        },
        dataSockets: {
            inputs: (cfg: NodeCfg<'varWriteMultiple'>): Record<`var${number}`, 'string' | 'boolean' | 'number' | 'varValue'> => {
                const inputEntries: [`var${number}`, 'varValue'][] = [];
                for (let i = 0; i < cfg.controlValues.vars.length; i++) {
                    inputEntries.push([ `var${i}`, 'varValue' ]);
                }
                return Object.fromEntries(inputEntries);
            },
            outputs: () => ({}),
        },
    },
    workInstruction: workInstructionNodeSocketCfg,
    workOrder: workOrderSocketCfg,
    workOrderSetStatus: workOrderSetStatusSocketCfg,
} satisfies {
    [kind in NodeKind]: NodeSocketCfg<NodeCfg<kind>>;
};

export const compatibilityMatrix: Record<NodeSocketKind, NodeSocketKind[]> = {
    anyData: [ 'anyData' ],
    boolean: [ 'anyData', 'boolean', 'varValue' ],
    event: [ 'event' ],
    number: [ 'anyData', 'number', 'varValue' ],
    string: [ 'anyData', 'string', 'varValue' ],
    varValue: [ 'anyData', 'varValue' ],
    workOrderMaterialData: [ 'anyData', 'workOrderMaterialData' ],
};

export function areSocketsCompatible (a: NodeSocketKind, b: NodeSocketKind): boolean {
    return compatibilityMatrix[a].includes(b);
}

export function getNodeSocketMaps<KIND extends NodeKind> (cfg: NodeCfg<KIND>) {
    const dataSocketMapItem = nodeSocketKindMap[cfg.kind].dataSockets as NodeSocketDef<NodeCfg<KIND>, NodeSocketKind>;
    const controlSocketMapItem = nodeSocketKindMap[cfg.kind].controlSockets as NodeSocketDef<NodeCfg<KIND>, NodeSocketKind>;
    return {
        inputs: {
            control: controlSocketMapItem.inputs(cfg),
            data: dataSocketMapItem.inputs(cfg),
        },
        outputs: {
            control: controlSocketMapItem.outputs(cfg),
            data: dataSocketMapItem.outputs(cfg),
        },
    };
}
